import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import KYCStatusManager from '../utils/kycStatusManager';
import KYCDebugHelper from '../utils/kycDebugHelper';
import KYCDataCleaner from '../utils/kycDataCleaner';
import './KYCStatus.css';

const KYCStatus = ({ merchantId, onStartKYC }) => {
  const { currentUser } = useAuth();
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchKYCStatus();
  }, [merchantId]);

  // Add a listener for storage changes to automatically refresh
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key && (e.key.includes('kyc') || e.key.includes('KYC'))) {
        console.log('🔄 KYCStatus: Storage changed, refreshing status');
        fetchKYCStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events
    const handleKYCUpdate = () => {
      console.log('🔄 KYCStatus: KYC update event received, refreshing status');
      fetchKYCStatus();
    };
    
    window.addEventListener('kycStatusUpdated', handleKYCUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('kycStatusUpdated', handleKYCUpdate);
    };
  }, []);

  const fetchKYCStatus = async () => {
    try {
      // Get consistent merchant ID
      const currentMerchantId = merchantId || currentUser?.id || currentUser?.merchantId || currentUser?.uid || 'demo-merchant';
      
      console.log('🔍 KYCStatus: Fetching status for merchant:', currentMerchantId);
      console.log('🔍 KYCStatus: Current user role:', currentUser?.role);
      
      // If user is admin, don't show any KYC status
      if (currentUser?.role === 'ADMIN') {
        console.log('ℹ️ KYCStatus: Admin user detected, not showing KYC status');
        setKycStatus(null);
        setLoading(false);
        return;
      }
      
      KYCDebugHelper.checkMerchantStatus(currentMerchantId);
      
      // Check localStorage first for persisted KYC status using utility
      const savedKycStatus = KYCStatusManager.getKYCStatus(currentMerchantId);
      
      if (savedKycStatus) {
        console.log('✅ KYCStatus: Found saved status:', savedKycStatus);
        setKycStatus(savedKycStatus);
        setLoading(false);
        return;
      }

      // Also check the old storage format for backward compatibility
      const legacyStatus = localStorage.getItem('kycStatus');
      if (legacyStatus) {
        try {
          const parsedStatus = JSON.parse(legacyStatus);
          console.log('✅ KYCStatus: Found legacy status:', parsedStatus);
          setKycStatus(parsedStatus);
          setLoading(false);
          return;
        } catch (e) {
          console.warn('Failed to parse legacy KYC status');
        }
      }

      console.log('ℹ️ KYCStatus: No saved status found, showing NOT_INITIATED status');

      // For merchants without any saved status, show NOT_INITIATED
      if (currentUser?.role === 'MERCHANT') {
        setKycStatus({
          status: 'NOT_INITIATED',
          fullName: currentUser?.fullName || currentUser?.name || '',
          businessName: currentUser?.businessName || '',
          panVerified: false,
          gstinVerified: false,
          registeredAddress: '',
          city: '',
          state: '',
          bankVerified: false,
          documentsUploaded: false,
          submittedAt: null,
          completionPercentage: 0
        });
      } else {
        // For non-merchant users, show null (no KYC needed)
        setKycStatus(null);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching KYC status:', error);
      setError('Failed to fetch KYC status');
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      'NOT_INITIATED': {
        color: '#6c757d',
        icon: '⏸️',
        title: 'KYC Not Started',
        description: 'Complete your KYC to start accepting payments'
      },
      'IN_PROGRESS': {
        color: '#ffc107',
        icon: '⏳',
        title: 'KYC In Progress',
        description: 'Your KYC application is being processed'
      },
      'PENDING_REVIEW': {
        color: '#17a2b8',
        icon: '👀',
        title: 'Under Review',
        description: 'Our team is reviewing your submitted documents'
      },
      'APPROVED': {
        color: '#28a745',
        icon: '✅',
        title: 'KYC Approved',
        description: 'Your account is fully verified and ready for transactions'
      },
      'REJECTED': {
        color: '#dc3545',
        icon: '❌',
        title: 'KYC Rejected',
        description: 'Please review the feedback and resubmit your documents'
      },
      'RESUBMISSION_REQUIRED': {
        color: '#fd7e14',
        icon: '🔄',
        title: 'Resubmission Required',
        description: 'Some documents need to be updated and resubmitted'
      }
    };

    return statusMap[status] || statusMap['NOT_INITIATED'];
  };

  const getCompletionPercentage = () => {
    if (!kycStatus) return 0;
    
    let completed = 0;
    const total = 6; // Total steps
    
    if (kycStatus.fullName && kycStatus.businessName) completed++;
    if (kycStatus.panVerified) completed++;
    if (kycStatus.registeredAddress && kycStatus.city && kycStatus.state) completed++;
    if (kycStatus.bankVerified) completed++;
    if (kycStatus.documentsUploaded) completed++;
    if (kycStatus.status === 'PENDING_REVIEW' || kycStatus.status === 'APPROVED') completed++;
    
    return Math.round((completed / total) * 100);
  };

  if (loading) {
    return (
      <div className="kyc-status-container">
        <div className="loading-spinner">Loading KYC status...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kyc-status-container">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  const status = kycStatus?.status || 'NOT_INITIATED';
  const statusInfo = getStatusInfo(status);
  const completionPercentage = getCompletionPercentage();

  return (
    <div className="kyc-status-container">
      <div className="kyc-status-card">
        <div className="status-header">
          <div className="status-icon" style={{ color: statusInfo.color }}>
            {statusInfo.icon}
          </div>
          <div className="status-info">
            <h3 style={{ color: statusInfo.color }}>{statusInfo.title}</h3>
            <p>{statusInfo.description}</p>
          </div>
        </div>

        {kycStatus && (
          <div className="progress-section">
            <div className="progress-header">
              <span>Completion Progress</span>
              <span className="progress-percentage">{completionPercentage}%</span>
            </div>
            <div className="progress-bar-container">
              <div 
                className="progress-bar-fill" 
                style={{ 
                  width: `${completionPercentage}%`,
                  backgroundColor: statusInfo.color 
                }}
              ></div>
            </div>
          </div>
        )}

        {kycStatus && (
          <div className="verification-checklist">
            <h4>Verification Checklist</h4>
            <div className="checklist-items">
              <div className={`checklist-item ${kycStatus.fullName && kycStatus.businessName ? 'completed' : ''}`}>
                <span className="check-icon">
                  {kycStatus.fullName && kycStatus.businessName ? '✅' : '⭕'}
                </span>
                <span>Business Information</span>
              </div>
              <div className={`checklist-item ${kycStatus.panVerified ? 'completed' : ''}`}>
                <span className="check-icon">
                  {kycStatus.panVerified ? '✅' : '⭕'}
                </span>
                <span>PAN Verification</span>
              </div>
              <div className={`checklist-item ${kycStatus.gstinVerified ? 'completed' : 'optional'}`}>
                <span className="check-icon">
                  {kycStatus.gstinVerified ? '✅' : '○'}
                </span>
                <span>GST Verification (Optional)</span>
              </div>
              <div className={`checklist-item ${kycStatus.registeredAddress ? 'completed' : ''}`}>
                <span className="check-icon">
                  {kycStatus.registeredAddress ? '✅' : '⭕'}
                </span>
                <span>Address Information</span>
              </div>
              <div className={`checklist-item ${kycStatus.bankVerified ? 'completed' : ''}`}>
                <span className="check-icon">
                  {kycStatus.bankVerified ? '✅' : '⭕'}
                </span>
                <span>Bank Account Verification</span>
              </div>
              <div className={`checklist-item ${kycStatus.documentsUploaded ? 'completed' : ''}`}>
                <span className="check-icon">
                  {kycStatus.documentsUploaded ? '✅' : '⭕'}
                </span>
                <span>Document Upload</span>
              </div>
            </div>
          </div>
        )}

        {kycStatus?.rejectionReason && (
          <div className="rejection-feedback">
            <h4>Feedback</h4>
            <p>{kycStatus.rejectionReason}</p>
          </div>
        )}

        <div className="action-buttons">
          {(!kycStatus || status === 'NOT_INITIATED') && (
            <button 
              className="btn btn-primary btn-lg"
              onClick={onStartKYC}
            >
              Start KYC Process
            </button>
          )}
          
          {status === 'IN_PROGRESS' && (
            <button 
              className="btn btn-primary btn-lg"
              onClick={onStartKYC}
            >
              Continue KYC
            </button>
          )}
          
          {(status === 'REJECTED' || status === 'RESUBMISSION_REQUIRED') && (
            <button 
              className="btn btn-warning btn-lg"
              onClick={onStartKYC}
            >
              Update KYC
            </button>
          )}
          
          {status === 'APPROVED' && (
            <div className="approved-message">
              <p>🎉 Your account is fully verified and ready for business!</p>
              <button 
                className="btn btn-secondary"
                onClick={() => window.location.href = '/dashboard'}
              >
                Go to Dashboard
              </button>
            </div>
          )}

          {/* Demo reset button for testing */}
          {kycStatus && (
            <div className="demo-actions">
              <button 
                className="btn btn-outline btn-sm"
                onClick={() => {
                  const currentMerchantId = merchantId || currentUser?.id || currentUser?.merchantId || currentUser?.uid || 'demo-merchant';
                  KYCStatusManager.clearKYCStatus(currentMerchantId);
                  // Also clear legacy storage
                  localStorage.removeItem('kycStatus');
                  localStorage.removeItem('kycVerificationComplete');
                  setKycStatus(null);
                  if (window.showNotification) {
                    window.showNotification('KYC status reset for demo purposes', 'info');
                  }
                }}
                style={{ marginTop: '10px', marginRight: '10px' }}
              >
                🔄 Reset KYC Status (Demo)
              </button>
              
              <button 
                className="btn btn-outline btn-sm"
                onClick={() => {
                  const currentMerchantId = merchantId || currentUser?.id || currentUser?.merchantId || currentUser?.uid || 'demo-merchant';
                  console.log('🔍 Debug KYC Status - Current Merchant ID:', currentMerchantId);
                  KYCDebugHelper.logAllKYCData();
                  KYCDebugHelper.checkMerchantStatus(currentMerchantId);
                  KYCDebugHelper.validateDataConsistency(currentMerchantId);
                }}
                style={{ marginTop: '10px', marginRight: '10px' }}
              >
                🐛 Debug KYC Data
              </button>
              
              <button 
                className="btn btn-outline btn-sm"
                onClick={() => {
                  const currentMerchantId = merchantId || currentUser?.id || currentUser?.merchantId || currentUser?.uid || 'demo-merchant';
                  const issues = KYCDataCleaner.checkForProblematicData();
                  if (issues.length > 0) {
                    const shouldFix = window.confirm(`Found ${issues.length} potential issues. Fix them automatically?`);
                    if (shouldFix) {
                      KYCDataCleaner.fixCommonIssues();
                      KYCDataCleaner.clearMerchantKYCData(currentMerchantId);
                      setKycStatus(null);
                      fetchKYCStatus();
                      if (window.showNotification) {
                        window.showNotification('KYC data issues fixed and reset', 'success');
                      }
                    }
                  } else {
                    if (window.showNotification) {
                      window.showNotification('No issues found with KYC data', 'info');
                    }
                  }
                }}
                style={{ marginTop: '10px' }}
              >
                🔧 Fix KYC Issues
              </button>
            </div>
          )}
        </div>

        {status === 'PENDING_REVIEW' && (
          <div className="review-timeline">
            <h4>Review Timeline</h4>
            <p>📅 Submitted: {new Date(kycStatus.submittedAt).toLocaleDateString()}</p>
            <p>⏱️ Expected completion: Within 24-48 hours</p>
            <p>📧 You'll receive an email notification once the review is complete.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KYCStatus;